import { AlertCircle, Lock } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** The one way this form shows that something has gone wrong. */
export const FieldError = ({ testId, children }) => (
  <p
    data-testid={testId}
    role="alert"
    className="mt-2.5 flex items-start gap-1.5 text-[13px] font-medium text-destructive"
  >
    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    {children}
  </p>
);

/**
 * One question's heading, hint and error, shared by every field below.
 *
 * Required questions carry an asterisk and optional ones say so, exactly as
 * the calculator's contact form does - a visitor should not have to guess
 * which answers we actually need.
 */
export const Field = ({ label, hint, error, children, testId, required = true }) => (
  <fieldset data-testid={testId} className="border-0 p-0">
    <legend className="text-[13px] font-bold uppercase tracking-[0.08em] text-foreground">
      {label}
      {required ? (
        <span className="ml-1 text-destructive" aria-hidden="true">
          *
        </span>
      ) : (
        <span className="ml-1.5 text-[11px] font-semibold normal-case tracking-normal text-muted-foreground">
          (optional)
        </span>
      )}
    </legend>
    {hint ? (
      <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{hint}</p>
    ) : null}
    <div className="mt-3.5">{children}</div>
    {error ? <FieldError testId={`${testId}-error`}>{error}</FieldError> : null}
  </fieldset>
);

/**
 * A typed answer: name, company, email, phone.
 *
 * Same properties as the calculator's contact gate - character limits, the
 * right keyboard on a phone, autofill hints, an asterisk on what we need and
 * an inline error the moment it matters.
 */
export const TextField = ({
  name,
  label,
  value,
  onChange,
  error,
  hint,
  placeholder,
  type = "text",
  autoComplete,
  inputMode = "text",
  maxLength,
  required = true,
}) => {
  const id = `scoping-${name}`;
  const testId = `scoping-${name.replace(/_/g, "-")}`;
  return (
    <div className="flex flex-col">
      <Label htmlFor={id} className="text-[13px] font-semibold text-foreground">
        {label}
        {required ? (
          <span className="ml-1 text-destructive" aria-hidden="true">
            *
          </span>
        ) : (
          <span className="ml-1.5 text-xs font-normal text-muted-foreground">(optional)</span>
        )}
      </Label>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      <Input
        id={id}
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        aria-invalid={error ? "true" : "false"}
        aria-required={required ? "true" : "false"}
        className={`mt-2 h-12 bg-white ${error ? "border-destructive" : ""}`}
      />
      {error ? <FieldError testId={`${testId}-error`}>{error}</FieldError> : null}
    </div>
  );
};

/** Permission to use the details, ticked by hand. Never a default. */
export const ConsentBox = ({ checked, onChange, error, text }) => (
  <div
    className={`rounded-xl border p-4 ${
      error ? "border-destructive bg-destructive/5" : "border-border bg-[color:var(--tt-surface-2)]"
    }`}
  >
    <div className="flex items-start gap-3">
      <Checkbox
        id="scoping-consent"
        data-testid="scoping-consent"
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
        aria-invalid={error ? "true" : "false"}
        className="mt-0.5 h-5 w-5 shrink-0 rounded-[3px] border-2 bg-white [&_svg]:h-4 [&_svg]:w-4"
      />
      <Label
        htmlFor="scoping-consent"
        className="cursor-pointer text-[13px] font-normal leading-relaxed text-muted-foreground"
      >
        {text}
        <span className="ml-1 font-semibold text-destructive" aria-hidden="true">
          *
        </span>
        <span className="sr-only"> (required)</span>
      </Label>
    </div>
    {error ? <FieldError testId="scoping-consent-error">{error}</FieldError> : null}
    <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
      <span>
        Stored securely in line with South Africa&apos;s Protection of Personal Information Act
        (POPIA). Used only to scope and discuss your finance function — never sold, never shared.
      </span>
    </p>
  </div>
);

const cardClasses = (selected) =>
  `flex min-h-[44px] cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-[14px] font-medium transition-colors duration-150 ${
    selected
      ? "border-[color:var(--tt-teal)] bg-[color:var(--tt-teal-mist)] text-foreground"
      : "border-border bg-white text-muted-foreground hover:border-[color:var(--tt-teal-line)] hover:text-foreground"
  }`;

/** A single-choice question as selectable cards rather than a native select. */
export const ChoiceCards = ({ name, options, value, onChange, columns = 2 }) => (
  <RadioGroup
    value={value || ""}
    onValueChange={onChange}
    className={`grid gap-2.5 ${columns === 1 ? "" : "sm:grid-cols-2"}`}
  >
    {Object.entries(options || {}).map(([code, label]) => {
      const id = `${name}-${code}`;
      const selected = value === code;
      return (
        <Label
          key={code}
          htmlFor={id}
          data-testid={`scoping-${name}-${code}`}
          data-selected={selected ? "true" : "false"}
          className={cardClasses(selected)}
        >
          <RadioGroupItem id={id} value={code} />
          {label}
        </Label>
      );
    })}
  </RadioGroup>
);

/**
 * A single-choice question with too many answers for cards.
 *
 * Industry is the only one: fourteen options as cards would swamp the step a
 * visitor is meant to finish in a few taps.
 */
export const ChoiceSelect = ({ name, options, value, onChange, placeholder, invalid = false }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger
      data-testid={`scoping-${name}-trigger`}
      aria-invalid={invalid ? "true" : "false"}
      className={`h-12 min-h-[44px] bg-white text-[14px] ${invalid ? "border-destructive" : ""}`}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {Object.entries(options || {}).map(([code, label]) => (
        <SelectItem key={code} value={code} data-testid={`scoping-${name}-${code}`}>
          {label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/** A multi-choice question, in the same card language. */
export const ChoiceChecks = ({ name, options, values, onToggle }) => (
  <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
    {Object.entries(options || {}).map(([code, label]) => {
      const id = `${name}-${code}`;
      const selected = (values || []).includes(code);
      return (
        <Label
          key={code}
          htmlFor={id}
          data-testid={`scoping-${name}-${code}`}
          data-selected={selected ? "true" : "false"}
          className={cardClasses(selected)}
        >
          <Checkbox id={id} checked={selected} onCheckedChange={() => onToggle(code)} />
          {label}
        </Label>
      );
    })}
  </div>
);
